import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Button,
  Grid,
  CircularProgress,
  TextField,
} from "@mui/material";
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ImageIcon from '@mui/icons-material/Image';
import useAxios from "../../../../hooks/useAxios";
import "./UploadFileModal.css";
import StyledTextField from "../../../common/StyledComponents/StyledTextField";

const UploadFileModal = ({ open, onClose, classId  }) => {
  const [title, setTitle] = useState(null);
  const [file, setFile] = useState(null);
  const [coverImage, setCoverImage] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const axiosInstance = useAxios();

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const handleCoverImageChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
        setCoverImage(e.target.files[0]);
    }
};


  const handleUpload = async () => {
    setError(null);
    setLoading(true);

    if (!title) {
      setError("Please choose a Title.");
      setLoading(false);
      return;
    }

    if (!file) {
      setError("Please choose a PDF file to upload.");
      setLoading(false);
      return;
    }

    if (file.type !== "application/pdf") {
      setError("The selected file must be a PDF.");
      setLoading(false);
      return;
    }

    if (coverImage && !["image/jpeg", "image/png", "image/jpg", "image/ico"].includes(coverImage.type)) {
      setError("The cover image must be a JPG, JPEG, PNG, or ICO file.");
      setLoading(false);
      return;
    }

    try {
      const formData = new FormData();
      formData.append("title", title);
      formData.append("file", file);
      if (classId) {
        formData.append("class_id", classId);
      }
      if (coverImage instanceof File) {
        formData.append("cover_image", coverImage);
    }
    

      const response = await axiosInstance.post("/quizzes/upload-pdf", formData);

      if (response.data.success) {
        const quizId = response.data.quiz_id;
        onClose(); // Close the modal
        window.location.href = `/quiz-review/${quizId}`; // Redirect to quiz review
      }
    } catch (err) {
      setError(err.response?.data?.message || "An unexpected error occurred.");
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setCoverImage(null);
    setError(null);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="upload-file-modal-title"
      aria-describedby="upload-file-modal-description"
    >
      <Box className="modal-container">
        <Typography variant="h6" className="modal-title">
          Upload Your Quiz
        </Typography>

        <StyledTextField
          label="Title"
          name="title"
          value={title}
          onChange={ e => setTitle(e.target.value)}
          fullWidth
          margin="normal"
          required
        />

        <Typography variant="body1" className="file-status">
          {file ? `Chosen File: ${file.name}` : "No chosen file"}
        </Typography>

        {!file && (
          <Button
            className="primary-button modal-btn"
            onClick={() => document.getElementById("file-input").click()}
          >
            <PictureAsPdfIcon style={{ marginRight: 8 }} /> Choose PDF File
          </Button>
        )}

        {file && (
          <>
            <Button
              className="secondary-button modal-btn"
              onClick={() => document.getElementById("file-input").click()}
            >
              <PictureAsPdfIcon style={{ marginRight: 8 }} /> Change File
            </Button>

            <Typography variant="body1" className="file-status">
              {coverImage ? `Cover Image: ${coverImage.name}` : "No cover image selected"}
            </Typography>

            <Button
              className="primary-button modal-btn"
              onClick={() => document.getElementById("cover-image-input").click()}
            >
              <ImageIcon style={{ marginRight: 8 }} /> Choose Cover Image
            </Button>
          </>
        )}

        {coverImage && (
          <Box className="image-preview">
            <img
              src={URL.createObjectURL(coverImage)}
              alt="Cover Preview"
              className="cover-image-preview"
            />
          </Box>
        )}

        {file && (
          <Button
            className="primary-button modal-btn"
            onClick={handleUpload}
            disabled={loading}
          >
            {loading ? 
            <>
                <CircularProgress size={24} /> 
                Extracting Questions
            </>
            : "Upload"}
          </Button>
        )}

        <input
          type="file"
          id="file-input"
          style={{ display: "none" }}
          accept="application/pdf"
          onChange={handleFileChange}
        />

        <input
            type="file"
            id="cover-image-input"
            style={{ display: "none" }}
            accept="image/jpeg, image/png, image/jpg, image/ico"
            onChange={handleCoverImageChange}
        />


        {error && (
          <Typography variant="body2" className="error-message">
            {error}
          </Typography>
        )}

        <Button
          className="secondary-button modal-btn"
          onClick={handleReset}
        >
          Reset
        </Button>
      </Box>
    </Modal>
  );
};

export default UploadFileModal;
